import React, { useEffect } from "react";
import { Button, Dropdown, Modal } from "react-materialize";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import {
  acceptPickup,
  getAvailablePickups,
  getOrders,
  getSingleOrder,
  ignorePickup,
} from "../../redux/action/pickups";
import { IWMURL } from "../../redux/config/urlConfig";
import { getDistance, notify } from "../../helpers/helper";
import { ClipLoader } from "react-spinners";
import CreateOrder from "./components/CreateOrder";
import OrderBox from "./components/OrderBox";
import OrderSearchBar from "./components/OrderSearchBar";
import { PickupStatus } from "./components/PickupStatus";
import { socket } from "../../redux/config/socket";
import SortOrderPickupsDropdown from "./components/SortOrderPickupsDropdown";
import {
  AcceptPickupRecycler,
  CounterPickupPrice,
  IgnorePickup,
} from "./components/IgnorePickup";
import { getNetwork } from "../../redux/action/organization";

const PickupsReycler = () => {
  const { availablePickups, accepting, loading } = useSelector(
    (state) => state.pickups
  );
  const { organization } = useSelector((state) => state.organization);
  const [location, setLocation] = React.useState("");
  const [globalLocation, setGlobalLocation] = React.useState("");
  const { orders, orderLoading } = useSelector((state) => state.pickups);
  const [order, setOrder] = React.useState({});
  const [ordersToDisplay, setOrdersToDisplay] = React.useState([]);
  const navigate = useNavigate();
  const pickupId = new URLSearchParams(window.location.search).get("pickupId");

  const dispatch = useDispatch();

  // Helper function to find order for a pickup (handles missing item.order)
  const findOrderForPickup = (pickup) => {
    // First try to find order by item.order (direct reference)
    let foundOrder = orders.find((o) => o._id === pickup.order);

    // If order not found, try to find the order by checking which order's pickups array contains this pickup
    if (!foundOrder) {
      foundOrder = orders.find((o) => {
        if (!o.pickups || !Array.isArray(o.pickups)) return false;

        // Try matching by _id first
        const matchById = o.pickups.some((p) => p._id === pickup._id);
        if (matchById) return true;

        // Fallback: match by scrap and generator if _id doesn't match
        const pickupScrapId = pickup.scrap?._id || pickup.scraps?.[0]?._id;
        const pickupGeneratorId = pickup.generator?._id;

        return o.pickups.some((p) => {
          const pScrapId = p.scrap?._id || p.scraps?.[0]?._id;
          const pGeneratorId = p.generator?._id;

          return (
            pickupScrapId &&
            pScrapId &&
            pickupScrapId === pScrapId &&
            pickupGeneratorId &&
            pGeneratorId &&
            pickupGeneratorId === pGeneratorId
          );
        });
      });
    }

    return foundOrder;
  };

  useEffect(() => {
    if (pickupId) {
      const pickup = availablePickups.find((p) => p._id === pickupId);
      if (pickup) {
        setOrder({
          _id: "all",
          pickups: availablePickups,
        });
      } else {
      }
    }
  }, [dispatch, pickupId, availablePickups]);

  useEffect(() => {
    dispatch(getAvailablePickups());
    dispatch(getOrders());
    dispatch(getNetwork());
  }, [dispatch]);

  useEffect(() => {
    if (!orders.length) return;

    setOrder({
      _id: "all",
      pickups: availablePickups,
    });

    setOrdersToDisplay(orders);
  }, [availablePickups, orders]);

  useEffect(() => {
    const onNotification = (msg) => {
      notify({
        title: msg.title,
        body: msg.body,
      });
      dispatch(getAvailablePickups());
      dispatch(getOrders());
    };
    socket.on("notification", onNotification);
    return () => socket.off("notification", onNotification);
  }, [dispatch]);

  return (
    <div className="p-1">
      <div className="flex align-center gap-1 mb-1 space-between ">
        <div className="flex align-center gap-1">
          <h3 className="bold margin-0">
            Material Sourcing Announcement & Pickups{" "}
          </h3>
          <Dropdown
            trigger={
              <div
                className="flex capitalize"
                style={{
                  border: "2px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px",
                  cursor: "pointer",
                }}
              >
                <p>
                  {organization.locations.find(
                    (loc) => loc._id === globalLocation
                  )?.nickname || "All Locations"}
                </p>
                <i className="material-symbols-outlined">keyboard_arrow_down</i>
              </div>
            }
          >
            <span
              onClick={() => {
                setGlobalLocation("");
                setOrdersToDisplay(orders);
                setOrder({
                  _id: "all",
                  pickups: availablePickups,
                });
              }}
            >
              All Locations
            </span>
            {organization.locations.map((location, index) => (
              <span
                value={location._id}
                key={index}
                onClick={() => {
                  setGlobalLocation(location._id);
                  const filtered = orders.filter(
                    (ord) => ord.location === location._id
                  );
                  setOrdersToDisplay(filtered);
                  const ids = filtered.map((ord) => ord._id);

                  setOrder({
                    _id: "all",
                    pickups: availablePickups.filter((pickup) =>
                      ids.includes(pickup.order)
                    ),
                  });
                }}
              >
                {location.nickname}-{location.address}
              </span>
            ))}
          </Dropdown>
        </div>

        <Modal
          trigger={
            <Button
              className="btn"
              tooltip="Post a material sourcing announcement to all of your networks"
            >
              Post New Announcement
            </Button>
          }
          actions={[]}
          id="create-order-modal"
        >
          <CreateOrder />
        </Modal>
      </div>

      <section className="flex gap-1">
        <div className="order-container">
          <OrderSearchBar
            setOrdersToDisplay={setOrdersToDisplay}
            ordersToDisplay={ordersToDisplay}
            orders={orders}
          />

          <div
            className={`order-all-pickups-btn ${order._id === "all" ? "order-box-selected green lighten-5" : ""
              }`}
            onClick={() => {
              setOrder({
                _id: "all",
                pickups: availablePickups,
              });
            }}
          >
            <p>All Available Pickup Requests</p>

            <div>
              <i className="material-symbols-outlined">double_arrow</i>
            </div>
          </div>

          {orderLoading || loading ? (
            <div className="flex justify-center full-width mt-2 mb-2">
              <ClipLoader color="#6f2da8" size={20} />
            </div>
          ) : !orders.length ? (
            <div className="flex justify-center">
              <div className="flex column align-center full-width  mv-2 p-2">
                <i
                  className="material-symbols-outlined grey-text"
                  style={{ fontSize: 80 }}
                >
                  inventory_2
                </i>
                <p className="grey-text mt-1 ">
                  You have no orders in your inventory.
                </p>
              </div>
            </div>
          ) : (
            ordersToDisplay.map((ord, index) => {
              return (
                <OrderBox
                  order={ord}
                  key={index}
                  setOrder={setOrder}
                  selectedOrder={order}
                />
              );
            })
          )}
        </div>

        <div className="order-pickups">
          {order._id === "all" ? (
            <div className="flex space-between align-center">
              <p className="bold">All Available Material Pickup Request</p>
              <SortOrderPickupsDropdown
                order={order}
                setOrder={setOrder}
                organization={organization}
              />
            </div>
          ) : (
            <div className="flex space-between align-center">
              <p className="">Pickups Associated With Announcement</p>

              <p className="bold green-text">#{order.orderNumber}</p>

              <p className="bold">All Available Material Pickup Request</p>

              <SortOrderPickupsDropdown
                order={order}
                setOrder={setOrder}
                organization={organization}
              />
            </div>
          )}
          <main>
            {loading ? (
              <div className="flex justify-center full-width mt-2 mb-2">
                <ClipLoader color="#6f2da8" size={20} />
              </div>
            ) : order.pickups?.length === 0 ? (
              <div className="flex justify-center full-width mt-2 mb-2">
                <div className="flex column align-center full-width  mv-2 p-2">
                  <i
                    className="material-symbols-outlined grey-text"
                    style={{ fontSize: 80 }}
                  >
                    inventory_2
                  </i>
                  <p className="grey-text mt-1 ">
                    You have no pickups in your inventory.
                  </p>
                </div>
              </div>
            ) : (
              <table className="striped  payment-history  ">
                <thead>
                  <tr>
                    <th>Info</th>
                    <th>Material Sourcing Announcement</th>
                    <th>Date</th>
                    <th>Location</th>
                    <th>Price & Quantity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {order.pickups?.map((item, index) => {
                    let scraps = item.scraps.length
                      ? item.scraps
                      : [item.scrap];

                    return scraps.map((scrap) => {
                      return (
                        <tr key={index}>
                          <td>
                            <span className="flex align-center gap-1 ">
                              <img
                                src={`${IWMURL}/scraps/read/image/${item.scrap._id}`}
                                className="scrap-image"
                                alt="scrap"
                              />

                              <div>
                                <strong className="cercle-purple-text">
                                  {item.generator?.name}
                                </strong>
                                <p>
                                  {scrap.name}-{scrap.variant}
                                </p>
                              </div>
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const associatedOrder = findOrderForPickup(item);
                              return associatedOrder ? (
                                <span className="bold green-text">
                                  #{associatedOrder.orderNumber}
                                </span>
                              ) : (
                                <span className="grey-text">No Announcement</span>
                              );
                            })()}
                          </td>
                          <td>{new Date(item.date).toDateString()}</td>
                          <td style={{}}>
                            <div>
                              <p className="cercle-purple-text">
                                {getDistance(
                                  item.pickupLocation?.latitude,
                                  item.pickupLocation?.longitude,
                                  organization.locations[0]?.latitude,
                                  organization.locations[0]?.longitude
                                ).toFixed(2)}{" "}
                                Km
                              </p>
                              <p>{item.pickupLocation?.address}</p>
                              <PickupStatus item={item} />
                            </div>
                          </td>
                          <td>
                            { }
                            <Modal
                              id={item._id + "-counter-price"}
                              trigger={
                                scrap?.priceChange?.processor?.to &&
                                !scrap?.priceChange?.generator?.to &&
                                !item.accepted && (
                                  <div
                                    className="align-center orange lighten-5  "
                                    style={{
                                      borderRadius: 10,
                                      border: "1px solid #ff9800",
                                      padding: 2,
                                      marginBottom: 5,
                                    }}
                                  >
                                    <p
                                      className="orange-text "
                                      style={{ fontSize: "10px" }}
                                    >
                                      Counter offer Sent
                                    </p>{" "}
                                  </div>
                                )
                              }
                              actions={[]}
                            >
                              <CounterPickupPrice
                                scrap={
                                  item.scraps.length
                                    ? item.scraps[0]
                                    : item.scrap
                                }
                                pickup={item}
                                order={order}
                                setOrder={setOrder}
                                index={index}
                              />
                            </Modal>
                            <Modal
                              id={item._id + "-counter-price"}
                              trigger={
                                scrap?.priceChange?.generator?.to &&
                                !item.accepted && (
                                  <div
                                    className="green-text align-center  green lighten-5 mt-1 "
                                    style={{
                                      borderRadius: 10,
                                      border: "1px solid #4caf50",
                                      padding: 2,
                                      marginBottom: 5,
                                    }}
                                  >
                                    <span
                                      className="green-text"
                                      style={{ fontSize: "10px" }}
                                    >
                                      Counter Offer Received
                                    </span>{" "}
                                  </div>
                                )
                              }
                              actions={[]}
                            >
                              <CounterPickupPrice
                                scrap={
                                  item.scraps.length
                                    ? item.scraps[0]
                                    : item.scrap
                                }
                                pickup={item}
                                order={order}
                                setOrder={setOrder}
                                index={index}
                              />
                            </Modal>
                            <p>{item.currency} {scrap.price}/Kg</p>
                            {item.quantity} {scrap.quantityUnit}
                          </td>

                          {item.accepted ? (
                            <td>
                              <button
                                className="btn-small white cercle-purple-text"
                                onClick={() => navigate("/pickup/" + item._id)}
                              >
                                View
                              </button>
                            </td>
                          ) : (
                            <td>
                              <div
                                className={
                                  "flex full-width align-center gap-1 "
                                }
                              >
                                <Modal
                                  id={item._id}
                                  trigger={
                                    <button className="btn waves-effect waves-purple">
                                      Accept
                                    </button>
                                  }
                                  actions={[]}
                                >
                                  <AcceptPickupRecycler
                                    acceptPickup={acceptPickup}
                                    accepting={accepting}
                                    item={item}
                                    setOrder={setOrder}
                                    setOrdersToDisplay={setOrdersToDisplay}
                                    dispatch={dispatch}
                                    getAvailablePickups={getAvailablePickups}
                                    getSingleOrder={getSingleOrder}
                                    getOrders={getOrders}
                                    index={index}
                                    location={location}
                                    notify={notify}
                                    organization={organization}
                                    setLocation={setLocation}
                                    scrap={scrap}
                                    orderLocation={order.location}
                                    order={order}
                                  />
                                </Modal>
                                <Modal
                                  id={item._id + "-counter-price"}
                                  trigger={
                                    <button
                                      className="modal-button white cercle-purple-text "
                                      style={{ border: "1px solid #6f2da8" }}
                                    >
                                      Counter
                                    </button>
                                  }
                                  actions={[]}
                                >
                                  <CounterPickupPrice
                                    scrap={
                                      item.scraps.length
                                        ? item.scraps[0]
                                        : item.scrap
                                    }
                                    pickup={item}
                                    order={order}
                                    setOrder={setOrder}
                                    index={index}
                                  />
                                </Modal>

                                <Modal
                                  id={"ignore-pickup-modal-" + index}
                                  trigger={
                                    <Link to="/" className="purple-text ml-1">
                                      Ignore
                                    </Link>
                                  }
                                >
                                  <IgnorePickup
                                    onClose={() => {
                                      const elem = document.getElementById(
                                        "ignore-pickup-modal-" + index
                                      );
                                      const instance =
                                        window.M.Modal.getInstance(elem);
                                      instance.close();
                                    }}
                                    onConfirm={(e) => {
                                      e.preventDefault();
                                      dispatch(ignorePickup(item._id));
                                    }}
                                  />
                                </Modal>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            )}
          </main>
        </div>
      </section>
    </div>
  );
};

export default PickupsReycler;
